import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { AppIconComponent } from '../icon/icon.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-datepicker-input',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxMaskDirective, AppIconComponent],
  templateUrl: './datepicker-input.component.html',
  styleUrl: './datepicker-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DatepickerInputComponent,
      multi: true,
    },
  ],
})
export class DatepickerInputComponent
  implements ControlValueAccessor, OnChanges, OnDestroy
{
  @Input() id = '';
  @Input() placeholder = 'dd/mm/aaaa';
  @Input() inputClass = 'form-control';
  @Input() readonly = false;
  @Input() errorMessages?: string | Record<string, string>;
  @Input() control: AbstractControl | null = null;
  /** Latest selectable date (ISO YYYY-MM-DD) for the calendar; typed dates rely on a control validator. */
  @Input() maxDate: string | null = null;
  /** Earliest selectable date (ISO YYYY-MM-DD) for the calendar. */
  @Input() minDate: string | null = null;

  @ViewChild('nativeDateInput') nativeDateInput?: ElementRef<HTMLInputElement>;

  displayValue = '';
  disabled = false;
  private controlSub?: Subscription;

  private onChange: (value: string | null) => void = () => { };
  private onTouched: () => void = () => { };

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control']) {
      this.bindControl();
    }
  }

  ngOnDestroy(): void {
    this.controlSub?.unsubscribe();
  }

  get hasError(): boolean {
    return !!this.control && this.control.touched && this.control.invalid;
  }

  get currentErrorMessage(): string {
    const errors = this.control?.errors;
    if (!errors) return '';

    if (typeof this.errorMessages === 'string') return this.errorMessages;

    if (this.errorMessages) {
      const firstKey = Object.keys(errors)[0];
      if (firstKey && this.errorMessages[firstKey]) return this.errorMessages[firstKey];
    }

    return 'Fecha no válida';
  }

  get showErrorMessage(): boolean {
    return this.hasError && !!this.currentErrorMessage;
  }

  get nativePickerValue(): string {
    if (!this.displayValue) return '';
    const iso = this.displayToIso(this.displayValue);
    return iso ?? '';
  }

  writeValue(value: string | null): void {
    this.setDisplayFromModel(value);
  }

  private setDisplayFromModel(value: string | null): void {
    if (!value) {
      this.displayValue = '';
      return;
    }

    if (typeof value !== 'string') {
      this.displayValue = '';
      return;
    }

    const normalized = this.normalizeIncomingDate(value);
    if (normalized && this.isIsoDate(normalized)) {
      this.displayValue = this.isoToDisplay(normalized);
      return;
    }

    const displayIso = this.displayToIso(String(normalized ?? value));
    if (displayIso) {
      this.displayValue = this.isoToDisplay(displayIso);
      return;
    }

    this.displayValue = '';
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onTextInput(value: string): void {
    this.displayValue = value;
    this.emitIsoOrNull();
  }

  onBlur(): void {
    this.onTouched();
  }

  openPicker(): void {
    if (this.disabled || this.readonly) return;
    const input = this.nativeDateInput?.nativeElement;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }
    input.click();
  }

  onNativeDateChange(value: string): void {
    if (!value) return;
    this.displayValue = this.isoToDisplay(value);
    this.onChange(value);
    this.onTouched();
  }

  onTextInputEvent(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.onTextInput(value);
  }

  onNativeDateChangeEvent(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.onNativeDateChange(value);
  }

  private emitIsoOrNull(): void {
    if (!this.displayValue) {
      this.onChange(null);
      return;
    }

    const iso = this.displayToIso(this.displayValue);
    this.onChange(iso);
  }

  private isIsoDate(value: unknown): boolean {
    if (typeof value !== 'string') return false;
    return this.normalizeIsoLike(value) !== null;
  }

  private isDisplayDate(value: unknown): boolean {
    if (typeof value !== 'string') return false;
    return this.displayToIso(value) !== null;
  }

  private displayToIso(value: string): string | null {
    if (typeof value !== 'string') return null;
    const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return null;

    const [, dd, mm, yyyy] = match;
    const day = Number(dd);
    const month = Number(mm);
    const year = Number(yyyy);

    if (!this.isValidDateParts(year, month, day)) return null;
    return `${yyyy}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private isoToDisplay(value: string): string {
    const normalized = this.normalizeIsoLike(value);
    if (!normalized) return '';

    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return '';

    const [, yyyy, mm, dd] = match;
    const day = Number(dd);
    const month = Number(mm);
    const year = Number(yyyy);

    if (!this.isValidDateParts(year, month, day)) return '';
    return `${dd}/${mm}/${yyyy}`;
  }

  private isValidDateParts(year: number, month: number, day: number): boolean {
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
      return false;
    }

    if (year < 1900 || year > 9999) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    const d = new Date(year, month - 1, day);
    return (
      d.getFullYear() === year &&
      d.getMonth() === month - 1 &&
      d.getDate() === day
    );
  }

  private normalizeIncomingDate(value: string): string | null {
    // Keep plain supported values untouched
    if (this.isIsoDate(value) || this.isDisplayDate(value)) return value;

    // Accept DateTime-like strings from API, e.g. 2026-02-01T00:00:00
    const isoLike = this.normalizeIsoLike(value);
    if (isoLike) return isoLike;

    return null;
  }

  private normalizeIsoLike(value: string): string | null {
    if (typeof value !== 'string') return null;
    const match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (!match) return null;

    const [, y, m, d] = match;
    const year = Number(y);
    const month = Number(m);
    const day = Number(d);

    if (!this.isValidDateParts(year, month, day)) return null;
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private bindControl(): void {
    this.controlSub?.unsubscribe();
    if (!this.control) return;

    this.setDisplayFromModel(this.control.value as string | null);
    this.controlSub = this.control.valueChanges.subscribe((val) => {
      this.setDisplayFromModel(val as string | null);
    });
  }
}
